import { CheckCircle2, Quote } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { STUDENT_MISSION, PARENT_ASKS, IMAGES } from "@/data/content";

const StudentsParents = () => (
  <section id="for-parents" data-testid="students-parents-section" className="py-20 lg:py-28 bg-white scroll-mt-24">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-24">
      {/* For Students */}
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <SectionHeading
          eyebrow="For Students"
          title="Your brain is not a school bag."
          description="You don't need to carry every fact around with you. Learning is not about filling your mind with more information — it is about learning how to Understand → Remember → Retrieve → Use. IQMIZE helps students build a systematic approach through practical routines, visualization, active recall, practice and review."
        />
        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-slate-200 bg-brand-light p-7 lg:p-9">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-brand">Your IQMIZE Mission</p>
            <ul className="mt-5 space-y-3.5" data-testid="student-mission-list">
              {STUDENT_MISSION.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 size={19} className="mt-0.5 shrink-0 text-brand" />
                  <span className="text-sm md:text-base font-semibold text-slate-800">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      {/* For Parents */}
      <div className="grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <SectionHeading
            eyebrow="IQMIZE Parent's Guide · For students below 18"
            title="Coach, not examiner."
            description="Parents play an important role in helping children build better learning habits. But the parent is not the examiner — the goal is to help the child retrieve, explain, practice and become progressively more independent."
          />
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            <div className="flex items-start gap-3">
              <Quote size={22} className="shrink-0 text-slate-300 rotate-180" />
              <div>
                <p className="text-sm font-semibold text-slate-400 line-through">
                  "You studied this yesterday. How can you forget it?"
                </p>
                <p className="mt-3 text-base font-bold text-inavy">
                  "That's okay. Let's see if we can bring it back."
                </p>
              </div>
            </div>
            <div className="mt-6 border-t border-slate-100 pt-5">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-4">Instead, ask</p>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="parent-asks">
                {PARENT_ASKS.map((q) => (
                  <p key={q} className="rounded-xl bg-brand-light px-4 py-3 text-sm font-semibold text-inavy">
                    "{q}"
                  </p>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <div className="space-y-8">
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-slate-200 bg-white p-3 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl bg-brand-light">
                <img
                  src={IMAGES.parent}
                  alt="Father and son reading the IQMIZE workbook together"
                  data-testid="parents-image"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="rounded-3xl bg-inavy p-8 lg:p-10 text-white shadow-xl" data-testid="parent-role-card">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-igold">Parent's Role</p>
              <div className="mt-8 space-y-8">
                <div>
                  <div className="flex items-end justify-between">
                    <p className="text-4xl font-extrabold">80%</p>
                    <p className="text-sm font-bold uppercase tracking-wider text-sky-200">Child</p>
                  </div>
                  <div className="mt-3 h-2.5 rounded-full bg-white/15 overflow-hidden">
                    <div className="h-full w-[80%] rounded-full bg-igold" />
                  </div>
                  <p className="mt-3 text-sm text-sky-100/90">Thinking, recalling, explaining, visualizing and practicing.</p>
                </div>
                <div>
                  <div className="flex items-end justify-between">
                    <p className="text-4xl font-extrabold">20%</p>
                    <p className="text-sm font-bold uppercase tracking-wider text-sky-200">Parent</p>
                  </div>
                  <div className="mt-3 h-2.5 rounded-full bg-white/15 overflow-hidden">
                    <div className="h-full w-[20%] rounded-full bg-sky-300" />
                  </div>
                  <p className="mt-3 text-sm text-sky-100/90">Prompting, encouraging, organizing and listening.</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export default StudentsParents;
